/**
 * Tabla de mensajes de usuario para los códigos de error del orquestador de Intervinientes.
 *
 *  - El backend sanea los errores antes de responder. Al cliente solo llegan:
 *      a) Códigos accionables (whitelist: validación de campos, estado/negocio, acceso/cesión,
 *         análisis documental) -> con su mensaje real.
 *      b) Tres cubos genéricos:
 *           INTEGRATION_SERVICE_UNAVAILABLE -> "Servicio de Integración no disponible" (transitorio)
 *           INTERVENER_OPERATION_FAILED     -> "Fallo en la sincronización del interviniente" (interno)
 *           MANUAL_REVIEW_REQUIRED          -> "Revisión manual requerida" (bloqueo AML / revisión)
 *  - El front nunca ramifica por `code` de error (eso lo hace con `success` y `flow.action`);
 *    esta tabla solo convierte `code` -> texto de usuario. Si el código no está, se usa el
 *    `message` del servidor como fallback (ya saneado).
 *  - Por diseño, esta tabla NO contiene códigos internos (DB_ERROR, OMNIA_*, NEXTPORTFOLIO_*,
 *    AML_*, *_ERROR, etc.): el backend ya no los envía; se colapsan en los tres cubos de arriba.
 */

// ---------------------------------------------------------------------------
// Tabla principal code → mensaje
// ---------------------------------------------------------------------------

const ERROR_MESSAGES: Record<string, string> =
{
    // --- Cubos genéricos (saneado del backend) ---
    INTEGRATION_SERVICE_UNAVAILABLE:
        "El servicio de integración no está disponible en este momento. Inténtalo de nuevo más tarde.",
    INTERVENER_OPERATION_FAILED:
        "No se ha podido completar la operación del interviniente. Si el problema persiste, contacta con el soporte en el botón de 'Necesitas ayuda'.",
    MANUAL_REVIEW_REQUIRED:
        "Tu solicitud requiere una revisión manual. Nuestro equipo se pondrá en contacto contigo.",

    // --- DNI / AnalyzeIdentityStatus ---
    DNI_IN_USE:
        "Este número de documento ya está registrado en la plataforma. Realiza una solicitud de acceso para operar con él.",
    DNI_EXTERNAL:
        "Este número de documento pertenece a un interviniente externo y no puede darse de alta ni vincularse.",
    DNI_UNKNOWN_STATE:
        "No se ha podido determinar el estado de este documento y no puedes proceder con la operación. Contacta con el soporte en el botón de 'Necesitas ayuda'.",
    EXISTS_ACTIVE_IN_USER:
        "Ya tienes un interviniente activo con este número de documento.",

    // --- Estado / negocio ---
    USER_IDENTITY_NOT_FOUND:
        "No se encontró el interviniente.",
    NOT_FOUND:
        "No se encontró el recurso solicitado.",
    FORBIDDEN:
        "No tienes permiso para realizar esta acción.",
    EXTERNAL_IDENTITY_NOT_EDITABLE:
        "Los intervinientes externos no pueden editarse.",
    INACTIVE_IDENTITY_NOT_EDITABLE:
        "Este interviniente está dado de baja. Para actualizar sus datos, contacta con el soporte en el botón de 'Necesitas ayuda'.",
    INVALID_STATUS:
        "El estado del interviniente o de la solicitud no permite realizar esta operación.",
    HAS_ACTIVE_CONTRACTS:
        "No se puede eliminar el interviniente porque tiene contratos activos.",
    IDENTITY_ALREADY_INACTIVE:
        "Este interviniente ya ha sido dado de baja.",
    ID_DOCUMENT_EXPIRED_RENEWAL_REQUIRED:
        "El documento de identidad está caducado. Sube el anverso y el reverso del DNI/NIE con una nueva fecha de validez.",
    INVALID_INPUT:
        "Falta un dato obligatorio para completar la operación.",
    MISSING_ID_DOCUMENT:
        "No se encontró el documento de identidad necesario para completar la operación.",

    // --- Acceso / cesión ---
    SELF_ACCESS_REQUEST:
        "No puedes solicitar acceso a tu propio interviniente.",
    SELF_OWNERSHIP_REQUEST:
        "No puedes ceder un interviniente que ya es tuyo.",
    ACCESS_REQUEST_ALREADY_EXISTS:
        "Ya existe una solicitud de acceso pendiente para este interviniente.",
    OWNERSHIP_REQUEST_ALREADY_EXISTS:
        "Ya existe una solicitud de cesión pendiente para este interviniente.",
    OWNER_IS_ACTIVE:
        "La cesión automática solo es posible cuando el propietario actual está inactivo.",
    ACCESS_NOT_FOUND:
        "No se encontró el acceso activo. Es posible que ya haya sido revocado.",
    ACCESS_REQUEST_ALREADY_RESOLVED:
        "Esta solicitud ya se había resuelto.",
    OWNERSHIP_TRANSFER_OWNER_CHANGED:
        "Ya no eres el propietario de este interviniente. Recarga las solicitudes.",
    ACCESS_REQUEST_SIGNATURE_REQUIRED:
        "Es necesario firmar para resolver la solicitud.",
    ACCESS_REQUEST_SIGNATURE_NOT_FOUND:
        "No se encontró la firma indicada.",
    ACCESS_REQUEST_SIGNATURE_NOT_VERIFIED:
        "La firma indicada ha caducado. Solicita un código nuevo.",
    ACCESS_REQUEST_SIGNATURE_NOT_OWNED:
        "La firma indicada no corresponde al propietario del interviniente.",
    ACCESS_REQUEST_SIGNATURE_ALREADY_USED:
        "La firma indicada ya se ha utilizado en otra solicitud.",
    // Cesión y cambio de principal: manda el mensaje del servidor (ver SERVER_MESSAGE_FIRST_CODES); el texto
    // de la tabla solo se usa si llega vacío.
    OWNERSHIP_TRANSFER_NOT_ALLOWED:
        "El titular actual no puede ceder este interviniente.",
    OWNERSHIP_TRANSFER_RECEIVER_WITHOUT_MAIN:
        "Tu cuenta no tiene interviniente principal, así que no puede recibir esta cesión.",
    MAIN_PROMOTION_NOT_ALLOWED:
        "No se puede completar esta operación.",
    MAIN_PROMOTION_BLOCKED:
        "No es posible completar esta operación. Contacta con soporte para resolverlo.",
    TRANSFER_PENDING_ACCEPTANCE:
        "Tienes que aceptar la cesión de este interviniente antes de operar con él.",

    // --- Validación de formulario (campo a campo) ---
    VALIDATION_ERROR:
        "Los datos no superan la validación. Revisa los campos indicados.",
    FIRST_NAME_REQUIRED:
        "El nombre es obligatorio.",
    LAST_NAME_REQUIRED:
        "El primer apellido es obligatorio.",
    ID_DOCUMENT_REQUIRED:
        "El número de documento es obligatorio.",
    EMAIL_REQUIRED:
        "El correo electrónico es obligatorio.",
    ID_TYPE_REQUIRED:
        "El tipo de documento es obligatorio.",
    PHONE_REQUIRED:
        "El teléfono es obligatorio.",
    PHONE_INVALID:
        "El formato del teléfono no es válido.",
    COUNTRY_PREFIX_REQUIRED:
        "El prefijo de país del teléfono es obligatorio.",
    POSTAL_ADDRESS_REQUIRED:
        "La dirección postal es obligatoria.",
    POSTAL_ADDRESS_INCOMPLETE:
        "La dirección postal está incompleta. Revisa el tipo de vía, el nombre y el número de la vía, el código postal, la ciudad y la provincia.",
    SAME_ADDRESS_REQUIRED:
        "Indica si la dirección fiscal es la misma que la postal.",
    FISCAL_ADDRESS_REQUIRED:
        "La dirección fiscal es obligatoria.",
    FISCAL_ADDRESS_INCOMPLETE:
        "La dirección fiscal está incompleta. Revisa el tipo de vía, el nombre y el número de la vía, el código postal, la ciudad y la provincia.",
    ID_FRONT_REQUIRED:
        "El anverso del documento de identidad es obligatorio.",
    ID_BACK_REQUIRED:
        "El reverso del documento de identidad es obligatorio.",
    ID_EXPIRATION_REQUIRED:
        "La fecha de caducidad del documento es obligatoria.",
    ID_EXPIRATION_EXPIRED:
        "El documento de identidad ha caducado.",
    BIRTHDAY_REQUIRED:
        "La fecha de nacimiento es obligatoria.",
    BIRTHDAY_IN_FUTURE:
        "La fecha de nacimiento no puede ser futura.",
    RESIDENCE_COUNTRY_NOT_ALLOWED:
        "El país de residencia indicado no está permitido para este interviniente.",
    FATCA_STATUS_NOT_ALLOWED:
        "El estado FATCA indicado no está permitido para este interviniente.",
    PROOF_OF_MINOR_GUARDIAN_REQUIRED:
        "El documento de representación legal del menor es obligatorio.",
    MAIN_CANNOT_UNSET:
        "No puedes dejar de ser el interviniente principal.",
    MAIN_CANNOT_EDIT_PERSONAL_DATA:
        "No se puede modificar el correo electrónico del interviniente principal.",
    ACTIVE_DNI_CANNOT_EDIT_DOCUMENT_DATA:
        "Un interviniente activo no puede modificar el número ni el tipo de documento.",

    // --- Análisis de documento (auxiliar, no bloqueante) ---
    INVALID_FILE:
        "El fichero enviado no es válido. Comprueba el formato y el tamaño, y vuelve a intentarlo.",
    AI_NO_DOC:
        "No se detectó ningún documento válido en las imágenes. Asegúrate de que la foto es clara y vuelve a intentarlo.",
    AI_LOW_CONFIDENCE:
        "El análisis del documento tiene baja confianza. Revisa, corrige y rellena los datos extraídos.",
    EMPTY_CONTENT:
        "El fichero enviado está vacío. Selecciona un fichero válido.",

    // --- Red / infraestructura (sintéticos del cliente) ---
    NETWORK_ERROR:
        "Error de red. Comprueba tu conexión e inténtalo de nuevo.",
    INVALID_RESPONSE:
        "Respuesta inesperada del servidor. Inténtalo de nuevo.",
};

// ---------------------------------------------------------------------------
// Función principal de resolución
// ---------------------------------------------------------------------------

/**
 * Códigos paraguas cuyo detalle (qué campo y por qué) solo viaja en el `message` del servidor. Para
 * ellos el texto de la tabla es solo un fallback: sin esto, cuatro errores distintos del validador del
 * borrador se mostraban como cuatro copias de "Los datos no superan la validación".
 */
const SERVER_MESSAGE_FIRST_CODES = new Set<string>(
[
    "VALIDATION_ERROR",
    "OWNERSHIP_TRANSFER_NOT_ALLOWED",
    "OWNERSHIP_TRANSFER_RECEIVER_WITHOUT_MAIN",
    "MAIN_PROMOTION_NOT_ALLOWED",
    "MAIN_PROMOTION_BLOCKED",
    "TRANSFER_PENDING_ACCEPTANCE",
]);

/**
 * Devuelve un mensaje amigable para el usuario a partir de un `code` de error del orquestador.
 * Si el código no está en la tabla, devuelve el `serverMessage` original como fallback.
 */
export function resolveErrorMessage(code: string, serverMessage: string): string
{
    if(SERVER_MESSAGE_FIRST_CODES.has(code) && serverMessage?.trim())
    {
        return serverMessage.trim();
    }
    return ERROR_MESSAGES[code] ?? serverMessage;
}

/**
 * Indica si un código de error corresponde a un aviso no bloqueante (warning).
 * En estos casos `success` puede ser `true` en el envelope.
 */
export function isWarningCode(code: string): boolean
{
    return code === "EXISTS_ACTIVE_IN_USER";
}